import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { UserPlus, Trash2, Users, AlertTriangle, Copy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const SuperAdminSalesTeam = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchTeam(); }, []);

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const r = await api.saListSalesTeam();
      setTeam(r.data || []);
    } catch {
      toast.error('Kunde inte hämta sälj-team');
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!form.name || !form.email) { toast.error('Namn och e-post krävs'); return; }
    setSaving(true);
    try {
      const r = await api.saCreateSalesUser(form);
      toast.success(`${r.data.name} skapad med säljkod ${r.data.sales_code}`);
      setShowForm(false);
      setForm({ name: '', email: '', phone: '' });
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa säljare');
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Radera säljare "${u.name}"? Deras tidigare skapade kunder behålls.`)) return;
    try {
      await api.saDeleteSalesUser(u.id);
      toast.success('Säljare borttagen');
      fetchTeam();
    } catch {
      toast.error('Kunde inte radera');
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="sa-sales-team-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users size={22} className="text-[#E8613A]" /> Säljare
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {team.length} säljare i Operio Sales-portalen. Varje säljare får sin egen säljkod och dashboard.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full" data-testid="sa-sales-new-btn">
          <UserPlus size={14} className="mr-1.5" /> Ny säljare
        </Button>
      </div>

      {loading ? (
        <p className="text-center text-sm text-muted-foreground py-10">Laddar…</p>
      ) : team.length === 0 ? (
        <div className="bg-card border-2 border-dashed border-border rounded-xl p-10 text-center">
          <Users size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Inga säljare ännu. Klicka på "Ny säljare" för att lägga upp första kontot.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {team.map(u => (
            <div key={u.id} className="bg-card border border-border rounded-xl p-5" data-testid={`sa-sales-team-${u.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-foreground">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  <div className="inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 bg-[#E8613A]/10 rounded">
                    <span className="text-[10px] uppercase tracking-wider text-[#E8613A] font-bold">Säljkod</span>
                    <span className="font-mono text-xs font-semibold text-[#E8613A]">{u.sales_code}</span>
                    <button onClick={() => { navigator.clipboard.writeText(u.sales_code); toast.success('Säljkod kopierad'); }} className="p-0.5 hover:bg-[#E8613A]/20 rounded">
                      <Copy size={10} className="text-[#E8613A]" />
                    </button>
                  </div>
                </div>
                <button onClick={() => remove(u)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950 text-muted-foreground hover:text-red-600" data-testid={`sa-sales-delete-${u.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-border/50">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Kunder</p>
                  <p className="text-lg font-bold">{u.customers_count}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">MRR</p>
                  <p className="text-lg font-bold">{Math.round(u.mrr).toLocaleString('sv-SE')} kr</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Skapa säljarkonto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Namn *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mikael Svensson" className="mt-1" data-testid="sa-sales-form-name" />
            </div>
            <div>
              <Label className="text-xs">E-post *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="sa-sales-form-email" />
            </div>
            <div>
              <Label className="text-xs">Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" />
            </div>
            <div className="text-xs bg-muted/40 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle size={12} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                Säljkod genereras automatiskt baserat på namnet (t.ex. SLS-MS01). Aktiveringsmail med "Sätt lösenord"-länk skickas direkt.
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Avbryt</Button>
            <Button onClick={save} disabled={saving} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="sa-sales-form-save">
              {saving ? 'Skapar…' : 'Skapa säljare'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminSalesTeam;
